/* ===========================================================================
 * Fisho · PDP / global modules — consolidated from 3 inline <script> blocks.
 *
 * Why this file exists:
 *   Previously each module was inlined inside its render snippet, which meant
 *   ~10 KB of JS shipped inside every page's HTML and re-parsed on every load.
 *   Moving them out to a deferred external file lets the browser cache once
 *   and reuse across the whole site, plus shrinks the rendered HTML.
 *
 * Each module is wrapped in its own IIFE and bails early via the
 * `if (!element) return;` guard, so this file is safe to load on any page —
 * modules only do work where their target elements exist.
 *
 * Modules in load order:
 *   1. Mobile Add-to-Cart bar           (#FishoMobileATC)
 *   2. Exit-intent popup                 (#FishoExitPopup)
 *   3. Locale suggest banner             (#FishoLocaleSuggest)
 *
 * Read-only data passed via DOM attributes (e.g. data-current-lang on the
 * banner) — never via inline Liquid here, so this file stays fully static.
 * ========================================================================= */

/* -------------------- 0. Eager video preload on first user intent --------------------
 * Why: the video sections (fisho-sticky-reveal, fisho-feature-spotlight, fisho-final-cta)
 * render <video preload="metadata"> directly in the DOM. With preload="metadata" the
 * browser only fetches ~50 KB of moov atom + the poster image — page load is fast.
 *
 * To make playback feel instant when the user actually scrolls to a section, we listen
 * for the FIRST sign of user intent (scroll OR touch OR keypress), then bump preload
 * to "auto" on every video tagged data-fisho-video-eager. The browser starts fetching
 * the full MP4 in parallel with whatever the user is doing — by the time IntersectionObserver
 * fires play() ~1-2 viewports later, the video is buffered enough to play smoothly.
 *
 * The trade-off vs old <template> approach: page load is ~500 KB heavier (posters + metadata
 * for ~3-4 videos), but mobile UX is dramatically better — videos no longer stuck on poster
 * for 3-5 seconds while MP4 streams from zero.
 * ============================================================================ */
(function () {
  /* Respect Data Saver mode and slow connections — don't burn user data. */
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn) {
    if (conn.saveData) return;
    if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return;
  }

  var bumped = false;
  function bumpAllVideoPreload() {
    if (bumped) return;
    bumped = true;
    var vids = document.querySelectorAll('video[data-fisho-video-eager]');
    for (var i = 0; i < vids.length; i++) {
      try {
        vids[i].preload = 'auto';
        /* explicit load() forces the browser to honor the new preload immediately */
        vids[i].load();
      } catch (e) {}
    }
  }
  /* Any of these signals "user is engaged" — kick off the buffering. {once: true} keeps it cheap. */
  var opts = { once: true, passive: true };
  window.addEventListener('scroll',     bumpAllVideoPreload, opts);
  window.addEventListener('touchstart', bumpAllVideoPreload, opts);
  window.addEventListener('keydown',    bumpAllVideoPreload, opts);
  window.addEventListener('mousemove',  bumpAllVideoPreload, opts);
  /* Fallback: if user is idle for 3 seconds without interacting, start buffering anyway —
     by then the page has paint-stabilized and we can afford the bandwidth. */
  setTimeout(bumpAllVideoPreload, 3000);
})();

/* -------------------- 1. Mobile Add-to-Cart bar -------------------- */
(function () {
  var bar = document.getElementById('FishoMobileATC');
  if (!bar) return;

  var btn = document.getElementById('FishoMobileATCBtn');
  if (!btn) return;

  var MOBILE_BREAKPOINT = 750;
  var mainAtc = null;
  var observer = null;
  var isMainVisible = true;

  function findMainAtc() {
    return (
      document.querySelector('product-form button[type="submit"].product-form__submit') ||
      document.querySelector('.product-form__submit') ||
      document.querySelector('product-form[data-type="add-to-cart-form"]') ||
      document.querySelector('product-form') ||
      document.querySelector('#MainProduct .product-form') ||
      document.querySelector('.fisho-main-product .product-form') ||
      document.querySelector('main .product-form') ||
      document.querySelector('form[action$="/cart/add"]')
    );
  }

  function show() {
    if (window.innerWidth >= MOBILE_BREAKPOINT) return;
    if (isMainVisible) return;
    bar.classList.add('is-visible');
    bar.setAttribute('aria-hidden', 'false');
  }

  function hide() {
    bar.classList.remove('is-visible');
    bar.setAttribute('aria-hidden', 'true');
  }

  function setupObserver() {
    mainAtc = findMainAtc();
    if (!mainAtc || !('IntersectionObserver' in window)) return;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          isMainVisible = entry.isIntersecting;
          if (isMainVisible) {
            hide();
          } else if (window.innerWidth < MOBILE_BREAKPOINT) {
            show();
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -20px 0px' }
    );
    observer.observe(mainAtc);
  }

  function handleResize() {
    if (window.innerWidth >= MOBILE_BREAKPOINT) {
      hide();
    } else if (!isMainVisible) {
      show();
    }
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open(btn);
      return;
    }
    if (drawer) {
      drawer.classList.remove('is-empty');
      drawer.setAttribute('open', '');
    }
  }

  function handleAdd() {
    if (btn.disabled) return;
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    bar.classList.add('is-loading');
    btn.disabled = true;

    var body = {
      items: [{ id: parseInt(variantId, 10), quantity: 1 }],
      sections: 'cart-drawer,cart-icon-bubble',
      sections_url: window.location.pathname
    };

    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript'
      },
      body: JSON.stringify(body)
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.sections) {
          var drawerEl = document.querySelector('cart-drawer');
          if (drawerEl && data.sections['cart-drawer']) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(data.sections['cart-drawer'], 'text/html');
            var newInner = doc.querySelector('cart-drawer');
            if (newInner) {
              drawerEl.innerHTML = newInner.innerHTML;
              drawerEl.classList.remove('is-empty');
            }
          }
          var bubble = document.querySelector('#cart-icon-bubble');
          if (bubble && data.sections['cart-icon-bubble']) {
            var parser2 = new DOMParser();
            var doc2 = parser2.parseFromString(data.sections['cart-icon-bubble'], 'text/html');
            var newBubble = doc2.querySelector('#cart-icon-bubble');
            if (newBubble) {
              bubble.innerHTML = newBubble.innerHTML;
            }
          }
        }
        openCartDrawer();
      })
      .catch(function () { /* silent */ })
      .then(function () {
        bar.classList.remove('is-loading');
        btn.disabled = false;
      });
  }

  btn.addEventListener('click', handleAdd);
  window.addEventListener('resize', handleResize, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupObserver);
  } else {
    setupObserver();
  }
})();

/* -------------------- 2. Exit-intent popup -------------------- */
(function () {
  var popup = document.getElementById('FishoExitPopup');
  if (!popup) return;

  var SESSION_KEY = 'fisho_exit_seen_v1';
  var MOBILE_MIN = 900;
  var ARM_DELAY_MS = 10000;

  try {
    if (window.sessionStorage && sessionStorage.getItem(SESSION_KEY) === '1') return;
  } catch (e) {}

  var tpl = (document.body && document.body.className) || '';
  if (/template-cart|template-checkout/.test(tpl)) return;
  if (window.innerWidth < MOBILE_MIN) return;

  var closeBtn = document.getElementById('FishoExitClose');
  var form = document.getElementById('FishoExitForm');
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var armed = false;
  var opened = false;
  var lastFocus = null;

  function markSeen() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
  }

  function open() {
    if (opened) return;
    opened = true;
    lastFocus = document.activeElement;
    popup.classList.add('is-open');
    popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    markSeen();
    var emailInput = document.getElementById('FishoExitEmail');
    if (emailInput) {
      setTimeout(function () { try { emailInput.focus(); } catch (e) {} }, reduceMotion ? 0 : 300);
    }
  }

  function close() {
    popup.classList.remove('is-open');
    popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try { lastFocus.focus(); } catch (e) {}
    }
  }

  function onMouseOut(e) {
    if (!armed) return;
    if (e.clientY > 5) return;
    var target = e.relatedTarget || e.toElement;
    if (target) return;
    open();
  }

  function onKeyDown(e) {
    if (e.key === 'Escape' && popup.classList.contains('is-open')) close();
  }

  function onBackdropClick(e) {
    if (e.target === popup) close();
  }

  function onFormSubmit() {
    popup.classList.add('is-submitted');
  }

  closeBtn && closeBtn.addEventListener('click', close);
  popup.addEventListener('click', onBackdropClick);
  document.addEventListener('keydown', onKeyDown);
  form && form.addEventListener('submit', onFormSubmit);

  setTimeout(function () {
    armed = true;
    document.addEventListener('mouseout', onMouseOut);
  }, ARM_DELAY_MS);
})();

/* -------------------- 3. Locale suggest banner -------------------- */
(function () {
  var banner = document.getElementById('FishoLocaleSuggest');
  if (!banner) return;

  var supported = {
    en: { native: 'English',    prompt: 'Continue in English?' },
    de: { native: 'Deutsch',    prompt: 'Zu Deutsch wechseln?' },
    lv: { native: 'Latviešu',   prompt: 'Pārslēgt uz latviešu valodu?' },
    pl: { native: 'Polski',     prompt: 'Przełączyć na polski?' },
    lt: { native: 'Lietuvių',   prompt: 'Perjungti į lietuvių?' },
    fi: { native: 'Suomi',      prompt: 'Vaihda suomeksi?' },
    sv: { native: 'Svenska',    prompt: 'Byt till svenska?' },
    ru: { native: 'Русский',    prompt: 'Переключить на русский?' }
  };

  function getCookie(name) {
    var v = ('; ' + document.cookie).split('; ' + name + '=');
    return v.length === 2 ? v.pop().split(';').shift() : null;
  }
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + value + '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax';
  }

  if (getCookie('fisho_locale_dismissed') === '1') return;

  var current = (banner.dataset.currentLang || 'en').toLowerCase();
  var browser = (navigator.language || 'en').slice(0, 2).toLowerCase();

  if (browser === current || !supported[browser]) return;

  var text = document.getElementById('FishoLocaleSuggestText');
  var input = document.getElementById('FishoLocaleSuggestLang');
  text.textContent = supported[browser].prompt;
  input.value = browser;
  banner.hidden = false;
  requestAnimationFrame(function () { banner.classList.add('is-visible'); });

  function dismiss() {
    setCookie('fisho_locale_dismissed', '1', 365);
    banner.classList.remove('is-visible');
    setTimeout(function () { banner.hidden = true; }, 220);
  }

  document.getElementById('FishoLocaleSuggestKeep').addEventListener('click', dismiss);
  document.getElementById('FishoLocaleSuggestClose').addEventListener('click', dismiss);
  document.getElementById('FishoLocaleSuggestSwitch').addEventListener('click', function () {
    setCookie('fisho_locale_dismissed', '1', 365);
  });
})();
